/**
 * Phase 107 (Plan 107-08 / D-15) — multi-select, display-only timezone
 * picker for the SLA Pricing editor's grid header.
 *
 * Ported from the deleted `frontend/src/components/coverage/TimezonePicker.tsx`
 * (git show bec322bc), Phase 54 / COVERAGE-09, with one behavioural change:
 * the pinned first row shows `{baseZone} (base)`, sourced from a `baseZone`
 * prop, instead of a hardcoded Prague label (107-UI-SPEC.md Implementation
 * Note 2).
 *
 * This component has no write path onto stored cells at all: its only
 * output is `onChange(next: string[])`, called with the updated list of
 * *extra* selected zones. It accepts no mutation callback and never touches
 * painted-cell state, satisfying D-15 — switching the view must never
 * mutate what was saved.
 */
import { useState, useRef, useEffect } from 'react';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { CURATED_ZONES } from './tzUtils';

const CHIP_BASE =
  'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11.5px] font-medium rounded-md border border-ms-border bg-ms-surface text-ms-text-secondary hover:bg-ms-surface-2 disabled:opacity-60 disabled:cursor-not-allowed';

const CHIP_ACTIVE =
  'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11.5px] font-semibold rounded-md border border-ms-orange/40 bg-ms-orange/5 text-ms-orange-dark';

export interface TimezonePickerProps {
  /** The configuration's own base timezone — pinned, not toggleable. */
  baseZone: string;
  /** Extra zones currently selected for display (never includes baseZone). */
  selectedZones: string[];
  /** Called with the updated extra-zone list. No cell-mutation path exists. */
  onChange: (next: string[]) => void;
}

export function TimezonePicker({ baseZone, selectedZones, onChange }: TimezonePickerProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const isActive = selectedZones.length > 0;
  const label = isActive ? `Timezones (${selectedZones.length})` : 'Timezones';

  function toggleZone(zoneId: string) {
    const next = selectedZones.includes(zoneId)
      ? selectedZones.filter((z) => z !== zoneId)
      : [...selectedZones, zoneId];
    onChange(next);
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        data-testid="sla-pricing-timezone-picker"
        className={isActive ? CHIP_ACTIVE : CHIP_BASE}
      >
        <GlobeAltIcon className="h-3.5 w-3.5" aria-hidden="true" />
        {label}
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          aria-label="Display timezones"
          className="absolute top-full right-0 mt-1 z-20 min-w-[180px] rounded-md border border-ms-border bg-ms-surface shadow-lg py-1"
        >
          {/* Pinned base-zone row — always shown selected, not toggleable */}
          <div
            role="option"
            aria-selected={true}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-[11.5px] text-left bg-ms-surface-2 opacity-60 cursor-default"
          >
            <span className="truncate text-ms-text-secondary">{baseZone} (base)</span>
            <svg
              className="ml-auto h-3.5 w-3.5 text-ms-orange-dark shrink-0"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          {CURATED_ZONES.map((zone) => {
            const selected = selectedZones.includes(zone.id);
            return (
              <button
                key={zone.id}
                type="button"
                role="option"
                aria-selected={selected}
                data-testid={`sla-pricing-timezone-option-${zone.id}`}
                onClick={() => toggleZone(zone.id)}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-[11.5px] text-left hover:bg-ms-surface-2 ${
                  selected ? 'bg-ms-surface-2' : ''
                }`}
              >
                <span className="truncate text-ms-text-secondary">{zone.label}</span>
                {selected && (
                  <svg
                    className="ml-auto h-3.5 w-3.5 text-ms-orange-dark shrink-0"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TimezonePicker;
